import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { VideoWithTags, UserVideoProgress } from '../types/video';
import { History, Play, Clock, CheckCircle, Trash2, ArrowLeft } from 'lucide-react';

interface VideoWithProgress extends VideoWithTags {
  progress?: UserVideoProgress;
}

export default function VideoHistory() {
  const navigate = useNavigate();
  const [history, setHistory] = useState<VideoWithProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  async function loadHistory() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: progressData, error: progressError } = await supabase
        .from('user_video_progress')
        .select('*')
        .eq('user_id', user.id)
        .order('last_watched_at', { ascending: false });

      if (progressError) throw progressError;

      const videoIds = progressData?.map((p) => p.video_id) || [];

      if (videoIds.length === 0) {
        setHistory([]);
        setLoading(false);
        return;
      }

      const { data: videosData, error: videosError } = await supabase
        .from('videos')
        .select('*')
        .in('id', videoIds);

      if (videosError) throw videosError;

      const videosWithTags = await Promise.all(
        (videosData || []).map(async (video) => {
          const { data: tagData } = await supabase
            .from('video_tags')
            .select('tag_id, tags(*)')
            .eq('video_id', video.id);

          const progress = progressData?.find((p) => p.video_id === video.id);

          return {
            ...video,
            tags: tagData?.map((vt: any) => vt.tags) || [],
            progress,
          };
        })
      );

      videosWithTags.sort((a, b) => {
        const aDate = new Date(a.progress?.last_watched_at || 0);
        const bDate = new Date(b.progress?.last_watched_at || 0);
        return bDate.getTime() - aDate.getTime();
      });

      setHistory(videosWithTags);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  }

  async function clearHistory(videoId: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_video_progress')
        .delete()
        .eq('user_id', user.id)
        .eq('video_id', videoId);

      if (error) throw error;
      loadHistory();
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  }

  function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function getProgressPercent(video: VideoWithProgress): number {
    if (!video.progress || !video.duration) return 0;
    return Math.round((video.progress.progress_seconds / video.duration) * 100);
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading history...</p>
        </div>
      </div>
    );
  }

  const inProgress = history.filter((v) => v.progress && !v.progress.completed);
  const completed = history.filter((v) => v.progress?.completed);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/dashboard/library')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Library</span>
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <History size={36} className="text-blue-600" />
            Watch History
          </h1>
          <p className="text-gray-600">{history.length} videos watched</p>
        </div>

        {history.length > 0 ? (
          <div className="space-y-8">
            {inProgress.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Continue Watching</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {inProgress.map((video) => (
                    <div
                      key={video.id}
                      className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-gray-300"
                    >
                      <div
                        onClick={() => navigate(`/dashboard/videos/${video.id}`)}
                        className="cursor-pointer"
                      >
                        <div className="relative aspect-video bg-gray-100">
                          {video.thumbnail_url ? (
                            <img
                              src={video.thumbnail_url}
                              alt={video.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play size={48} className="text-gray-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                            <Play
                              size={48}
                              className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300">
                            <div
                              className="h-full bg-blue-600 transition-all"
                              style={{ width: `${getProgressPercent(video)}%` }}
                            />
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                            {video.title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDuration(video.duration)}
                            </span>
                            <span>{getProgressPercent(video)}% complete</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {video.progress && formatDate(video.progress.last_watched_at)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          clearHistory(video.id);
                        }}
                        className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-sm border border-gray-200 hover:bg-red-50 hover:text-red-600 transition-all opacity-0 group-hover:opacity-100"
                        title="Remove from history"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {completed.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle size={28} className="text-green-600" />
                  Completed
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {completed.map((video) => (
                    <div
                      key={video.id}
                      className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-gray-300"
                    >
                      <div
                        onClick={() => navigate(`/dashboard/videos/${video.id}`)}
                        className="cursor-pointer"
                      >
                        <div className="relative aspect-video bg-gray-100">
                          {video.thumbnail_url ? (
                            <img
                              src={video.thumbnail_url}
                              alt={video.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play size={48} className="text-gray-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                            <Play
                              size={48}
                              className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            />
                          </div>
                          <div className="absolute top-2 left-2">
                            <CheckCircle className="text-green-600 fill-current bg-white rounded-full" size={24} />
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                            {video.title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDuration(video.duration)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {video.progress && formatDate(video.progress.last_watched_at)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          clearHistory(video.id);
                        }}
                        className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-sm border border-gray-200 hover:bg-red-50 hover:text-red-600 transition-all opacity-0 group-hover:opacity-100"
                        title="Remove from history"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <History size={64} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No watch history yet</p>
            <p className="text-gray-400 mb-6">
              Start watching videos to track your progress here
            </p>
            <button
              onClick={() => navigate('/dashboard/videos')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
            >
              Browse Videos
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
